import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Loader2,
  BarChart3,
  Eye,
  Download,
  FileOutput,
  Users,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  FileText,
  RefreshCw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FluentCard } from '@/components/layout/page-header'
import { analyticsService } from '@/lib/api/services/analytics'
import { templateService, type TemplateOData } from '@/lib/api/services/templates'
import {
  AnalyticsDateRangePicker,
  UsageTrendChart,
  TopUsersTable,
  FieldCompletionChart,
  EventsTable,
  ExportButton,
} from '@/components/analytics'
import { cn } from '@/lib/utils'
import type {
  TemplateAnalytics,
  TopUserAnalytics,
  UsageTrendData,
  FieldCompletionData,
  TemplateUsageEvent,
  AggregationPeriod,
  AnalyticsFilters,
} from '@/lib/api/types'

export default function TemplateAnalyticsPage() {
  const { id } = useParams<{ id: string }>()

  // Date range state
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date()
    date.setDate(date.getDate() - 30)
    return date.toISOString().split('T')[0] ?? ''
  })
  const [endDate, setEndDate] = useState<string>(() => new Date().toISOString().split('T')[0] ?? '')
  const [period, setPeriod] = useState<AggregationPeriod>('Daily')

  // Data state
  const [template, setTemplate] = useState<TemplateOData | null>(null)
  const [analytics, setAnalytics] = useState<TemplateAnalytics | null>(null)
  const [usageTrends, setUsageTrends] = useState<UsageTrendData[]>([])
  const [topUsers, setTopUsers] = useState<TopUserAnalytics[]>([])
  const [fieldCompletion, setFieldCompletion] = useState<FieldCompletionData[]>([])
  const [events, setEvents] = useState<TemplateUsageEvent[]>([])

  // Loading states
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const filters: AnalyticsFilters = {
    startDate,
    endDate,
    period,
    templateId: id,
  }

  const loadAnalytics = async (isRefresh = false) => {
    if (!id) return

    try {
      if (isRefresh) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }
      setError(null)

      // Load all data in parallel
      const [templateData, analyticsData, trendsData, usersData, fieldData, eventsData] =
        await Promise.all([
          templateService.getTemplate(id).catch(() => null),
          analyticsService.getTemplateAnalyticsById(id, filters).catch(() => null),
          analyticsService.getUsageTrends(filters).catch(() => []),
          analyticsService.getTopUsers(filters, 10).catch(() => []),
          analyticsService.getFieldCompletionRates(id).catch(() => []),
          analyticsService.getTemplateEvents(id, filters).catch(() => []),
        ])

      setTemplate(templateData)
      setAnalytics(analyticsData)
      setUsageTrends(trendsData)
      setTopUsers(usersData)
      setFieldCompletion(fieldData)
      setEvents(eventsData)
    } catch (err) {
      setError('Failed to load analytics data')
      console.error('Failed to load template analytics:', err)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    loadAnalytics()
  }, [id, startDate, endDate, period])

  const handleDateChange = (start: string, end: string) => {
    setStartDate(start)
    setEndDate(end)
  }

  const handlePeriodChange = (newPeriod: AggregationPeriod) => {
    setPeriod(newPeriod)
  }

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
    return `${(ms / 60000).toFixed(1)}m`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="p-4 rounded-2xl bg-gray-100 dark:bg-gray-800">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </div>
    )
  }

  if (error || !template) {
    return (
      <div className="space-y-6">
        <Link
          to="/templates"
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Templates
        </Link>
        <FluentCard className="flex flex-col items-center justify-center py-12">
          <div className="p-4 rounded-2xl bg-red-100 dark:bg-red-900/30 mb-4">
            <BarChart3 className="h-8 w-8 text-red-500 dark:text-red-400" />
          </div>
          <p className="text-red-600 dark:text-red-400 mb-4 font-medium">
            {error || 'Template not found'}
          </p>
          <Button onClick={() => loadAnalytics()}>Retry</Button>
        </FluentCard>
      </div>
    )
  }

  const statCards = [
    {
      label: 'Total Views',
      value: analytics?.viewCount ?? 0,
      icon: Eye,
      color: 'from-blue-400 to-blue-600',
    },
    {
      label: 'Total Downloads',
      value: analytics?.downloadCount ?? 0,
      icon: Download,
      color: 'from-emerald-400 to-green-600',
    },
    {
      label: 'Documents Generated',
      value: analytics?.generationCount ?? 0,
      icon: FileOutput,
      color: 'from-violet-400 to-purple-600',
    },
    {
      label: 'Unique Users',
      value: analytics?.uniqueUsers ?? 0,
      icon: Users,
      color: 'from-amber-400 to-orange-600',
    },
    {
      label: 'Avg Duration',
      value: formatDuration(analytics?.averageDurationMs ?? 0),
      icon: Clock,
      color: 'from-pink-400 to-rose-600',
    },
    {
      label: 'Field Completion',
      value: `${(analytics?.fieldCompletionRate ?? 0).toFixed(0)}%`,
      icon: TrendingUp,
      color: 'from-cyan-400 to-teal-600',
    },
  ]

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to={`/templates/${id}`}
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Template
      </Link>

      {/* Template Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-gradient-to-br from-violet-400 to-purple-600 shadow-lg shadow-purple-500/25">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{template.name}</h1>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-gray-500 dark:text-gray-400">Analytics</span>
              {analytics?.trend !== undefined && (
                <span
                  className={cn(
                    'flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full',
                    analytics.trend > 0 &&
                      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                    analytics.trend < 0 &&
                      'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                    analytics.trend === 0 &&
                      'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  )}
                >
                  {analytics.trend > 0 && <TrendingUp className="h-3 w-3" />}
                  {analytics.trend < 0 && <TrendingDown className="h-3 w-3" />}
                  {analytics.trend === 0 && <Minus className="h-3 w-3" />}
                  {analytics.trend > 0 ? '+' : ''}
                  {analytics.trend.toFixed(1)}% vs previous period
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadAnalytics(true)}
            disabled={refreshing}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <ExportButton filters={filters} />
        </div>
      </div>

      {/* Date Range Picker */}
      <AnalyticsDateRangePicker
        startDate={startDate}
        endDate={endDate}
        period={period}
        onDateChange={handleDateChange}
        onPeriodChange={handlePeriodChange}
      />

      {/* Stats Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {statCards.map((stat) => (
          <FluentCard key={stat.label}>
            <div className="flex items-center gap-3">
              <div className={cn('p-2.5 rounded-xl bg-gradient-to-br shadow-sm', stat.color)}>
                <stat.icon className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
              </div>
            </div>
          </FluentCard>
        ))}
      </div>

      {/* Usage Trends */}
      <UsageTrendChart data={usageTrends} loading={refreshing} />

      {/* Two Column Layout */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Top Users */}
        <TopUsersTable
          data={topUsers}
          loading={refreshing}
          title="Active Users"
          description="Users who interacted with this template"
        />

        {/* Field Completion */}
        <FieldCompletionChart data={fieldCompletion} loading={refreshing} />
      </div>

      {/* Recent Events */}
      <EventsTable data={events} loading={refreshing} showTemplateColumn={false} />
    </div>
  )
}
